import React, { useState, useRef } from 'react';
import { UploadIcon, PersonIcon, ArrowLeftIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import GenerationProgress from './GenerationProgress';
import useScrollIntoView from './useScrollIntoView';
import { useNotifications } from './Notifications';
import { useI18n } from '../i18n';

const PersonRemover = ({ 
  user, 
  credits, 
  setCredits, 
  getAuthHeaders, 
  backendUrl, 
  onBack,
  setImageListRefresh,
  setShowPayment 
}) => {
  const { t } = useI18n();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const resultRef = useScrollIntoView(result);
  const { notifyWarning, notifyError, notifyApiError } = useNotifications();

  const acceptFile = (file) => {
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notifyWarning('That image is larger than 10MB. Please choose a smaller one.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      notifyWarning('That file is not an image. Please choose a JPG, PNG or GIF.');
      return;
    }

    setSelectedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageSelect = (event) => {
    acceptFile(event.target.files[0]);
  };

  // The upload area advertises drag and drop; without these handlers the browser
  // navigates away from the app and the user loses their session.
  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    acceptFile(event.dataTransfer?.files?.[0]);
  };

  const handleRemovePerson = async () => {
    if (isProcessing) return;

    if (!selectedImage || !prompt.trim()) {
      notifyWarning('Please select an image and describe the person to remove.');
      return;
    }

    if (!user) {
      notifyWarning('Please sign in to use Person Remover.');
      return;
    }

    // credits is null until the balance loads - only a real zero blocks the request.
    if (typeof credits === 'number' && credits <= 0) {
      setShowPayment(true);
      return;
    }

    setIsProcessing(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('prompt', prompt.trim());

      const authHeaders = await getAuthHeaders();
      const response = await axios.post(`${backendUrl}/api/remove-person`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...authHeaders
        },
      });

      setResult(response.data.result);
      setCredits(response.data.creditsRemaining);
      
      // Trigger image list refresh
      setImageListRefresh(prev => prev + 1);
    } catch (error) {
      console.error('Error removing person:', error);
      const described = notifyApiError(error, 'Could not process that image. Please try again.');
      // 400 and 402 are both used for insufficient credits today; the code is the durable signal.
      if (described.code === 'INSUFFICIENT_CREDITS' || described.status === 402 || described.status === 400) {
        setShowPayment(true);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSelection = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setPrompt('');
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadImage = (imageData, filename = 'person-removed.png') => {
    if (!imageData || !imageData.startsWith('data:image')) {
      notifyError('No image data available to download.');
      return;
    }
    
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="person-remover">
      <div className="feature-header">
        <button className="btn btn-secondary back-btn" onClick={onBack}>
          <ArrowLeftIcon style={{ marginRight: '8px' }} />
          {t('nav.backToDashboard')}
        </button>
        <div className="header-content">
          <div className="feature-icon">
            <PersonIcon style={{ width: '32px', height: '32px' }} />
          </div>
          <div>
            <h1>{t('tool.person.name')}</h1>
            <p>{t('tool.person.desc')}</p>
          </div>
        </div>
      </div>

      <div className="person-remover-content">
        {!selectedImage ? (
          <div className="upload-section">
            <div
              className={`upload-area${isDragActive ? ' drag-active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <UploadIcon style={{ width: '48px', height: '48px', marginBottom: '16px' }} />
              <h3>{t('upload.title')}</h3>
              <p>{t('upload.hint')}</p>
              <p className="upload-hint">{t('upload.formats')}</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div className="processing-section">
            <div className="image-preview-container">
              <img src={imagePreview} alt="Selected" className="selected-image" />
              <button 
                className="btn btn-secondary clear-btn"
                onClick={clearSelection}
              >
                Select Different Image
              </button>
            </div>

            <div className="prompt-section">
              <h3>Describe the person to remove:</h3>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Example: Remove the person furthest right in the image with the bordeaux sweater"
                className="prompt-textarea"
                rows={4}
                disabled={isProcessing}
              />
              
              <div className="action-buttons">
                <button
                  className="btn btn-primary process-btn"
                  onClick={handleRemovePerson}
                  disabled={isProcessing || !prompt.trim()}
                >
                  {isProcessing ? (
                    <>
                      <div className="spinner" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <PersonIcon style={{ marginRight: '8px' }} />
                      Remove Person (1 Credit)
                    </>
                  )}
                </button>
              </div>

              <div className="tips">
                <h4>💡 Tips for better results:</h4>
                <ul>
                  <li>Be specific about the person's location (left, right, center, background)</li>
                  <li>Mention distinctive clothing or features</li>
                  <li>Use clear, descriptive language</li>
                  <li>Higher resolution images work better</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {isProcessing && (
          <GenerationProgress
            title="Removing the person from your photo"
            estimateSeconds={25}
            steps={[
              'Uploading your photo...',
              'Sending it to the AI model...',
              'Removing the person and rebuilding the background...',
              'Rendering the final image...'
            ]}
          />
        )}

        {result && (
          <div className="result-container" ref={resultRef}>
            <h3>Person Removed:</h3>
            {result.startsWith('data:image') ? (
              <>
                <img 
                  src={result} 
                  alt="Person removed result" 
                  className="result-image"
                />
                <div className="result-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => downloadImage(result, `person-removed-${Date.now()}.png`)}
                  >
                    Download Result
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={clearSelection}
                  >
                    Remove Another Person
                  </button>
                </div>
              </>
            ) : (
              <p className="error-message">{result}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonRemover;